import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { apiService } from '../services/apiService';

export const GoogleCallbackPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { googleAuth } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(true);

  useEffect(() => {
    const handleCallback = async () => {
      try {
        const code = searchParams.get('code');
        const token = searchParams.get('token');
        const userId = searchParams.get('user_id');
        const error = searchParams.get('error');
        const errorDescription = searchParams.get('error_description');

        if (error) {
          console.error('Google OAuth error:', error, errorDescription);
          
          // Handle specific error types
          if (error === 'access_denied') {
            setError('Access denied. Please grant the necessary permissions to continue.');
          } else if (error === 'invalid_request') {
            setError('Invalid request. Please try logging in again.');
          } else if (error === 'unauthorized_client') {
            setError('Unauthorized client. Please contact support if this issue persists.');
          } else if (error === 'unsupported_response_type') {
            setError('Unsupported response type. Please contact support if this issue persists.');
          } else if (error === 'invalid_scope') {
            setError('Invalid scope. Please contact support if this issue persists.');
          } else if (error === 'server_error') {
            setError('Server error. Please try again later.');
          } else if (error === 'temporarily_unavailable') {
            setError('Service temporarily unavailable. Please try again later.');
          } else {
            setError(`Authentication error: ${errorDescription || error}. Please try again.`);
          }
          
          setIsProcessing(false);
          return;
        }

        // Case 1: Token is directly provided (from backend redirect)
        if (token && userId) {
          console.log('Received token from URL:', token);
          console.log('Received user_id from URL:', userId);
          
          // Store the token
          localStorage.setItem('authToken', token);
          console.log('Token stored in localStorage');
          
          // Set the token in the API service
          apiService.setToken(token);
          console.log('Token set in API service:', apiService.getToken());

          // Get user data from backend using the token
          try {
            console.log('Calling getUserProfile...');
            const userData = await apiService.getUserProfile();
            console.log('User data received:', userData);
            
            // Update auth context with user data
            await googleAuth({
              email: userData.email,
              name: `${userData.firstName} ${userData.lastName}`,
              given_name: userData.firstName,
              family_name: userData.lastName,
              picture: ''
            });

            // Redirect to dashboard
            navigate('/app/dashboard', { replace: true });
          } catch (userDataError) {
            console.error('Failed to get user data:', userDataError);
            setError('Failed to retrieve user information');
            setIsProcessing(false);
          }
          return;
        }

        // Case 2: Authorization code is provided (existing flow)
        if (!code) {
          setError('No authorization code or token received from Google');
          setIsProcessing(false);
          return;
        }

        // Send the authorization code to backend using API service
        const data = await apiService.handleGoogleOAuthCallback(code);

        // Store the token
        localStorage.setItem('authToken', data.token);

        // Update auth context with user data
        await googleAuth({
          email: data.user.email,
          name: `${data.user.firstName} ${data.user.lastName}`,
          given_name: data.user.firstName,
          family_name: data.user.lastName,
          picture: ''
        });

        // Redirect to dashboard
        navigate('/dashboard', { replace: true });

      } catch (err) {
        console.error('Google callback error:', err);
        
        // Handle specific API errors
        if (err instanceof Error) {
          if (err.message.includes('access_denied')) {
            setError('Access denied. Please grant the necessary permissions and try again.');
          } else if (err.message.includes('Failed to exchange authorization code')) {
            setError('Authentication failed. Please try logging in again.');
          } else if (err.message.includes('No access token received')) {
            setError('Authentication failed. Please try logging in again.');
          } else if (err.message.includes('Failed to get user information')) {
            setError('Failed to retrieve user information. Please try again.');
          } else if (err.message.includes('Email is required')) {
            setError('Email permission is required. Please grant email access and try again.');
          } else {
            setError(err.message);
          }
        } else {
          setError('Authentication failed. Please try again.');
        }
        
        setIsProcessing(false);
      }
    };

    handleCallback();
  }, [searchParams, navigate, googleAuth]);

  if (isProcessing) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <LoadingSpinner />
          <p className="mt-4 text-gray-600 dark:text-gray-300">Completing Google authentication...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <div className="mx-auto h-12 w-12 text-red-500">
              <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h2 className="mt-6 text-3xl font-extrabold text-gray-900 dark:text-white">
              Authentication Failed
            </h2>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
              {error}
            </p>
            <div className="mt-6">
              <button
                onClick={() => navigate('/login')}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Back to Login
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
}; 